import { z } from 'zod'
import type { Campaign } from '@dashboard/types/config'
import { audienceVersions } from '@shared/campaignTests/audienceTest'
import type { VersionId } from '@shared/campaignTests/audienceTest'
import type { CanvasController } from '../../composables/useEditorCanvas'
import type { ElementDetail, ResolutionReport, TargetDescriptor } from '@shared/editor/model'
import type { Audience } from './projection'
import { audienceTerms, campaignActions } from './campaignEdit'
import { supportsMatrixText, type Matrix } from './matrix'
import { isElementId, type ElementId } from '@shared/editor/model'
import { audienceDisplayName, type GroupCatalogEntry } from './groups'

const versionIdSchema = z.enum(['A', 'B', 'C', 'D'])
const matrixCellSchema = z.object({
  audienceId: z.string().min(1).max(128),
  versionId: versionIdSchema.optional(),
  elementId: z.string().min(1).max(200)
})
export const matrixSelectionSchema = z.object({
  cells: z.array(matrixCellSchema).min(1).max(2000),
  audiences: z
    .array(
      z.object({
        id: z.string().min(1).max(128),
        name: z.string().max(200),
        versions: z
          .array(z.object({ versionId: versionIdSchema, label: z.string().max(200) }))
          .max(4)
          .optional(),
        groups: z.array(z.object({ id: z.string().max(128), name: z.string().max(200) })).max(200)
      })
    )
    .max(2000),
  opening: z.literal(true).optional()
})
export type MatrixSelectionRequest = {
  cells: z.infer<typeof matrixCellSchema>[]
  pageUrl: string
  opening?: true
}

const selector = z.string().min(1).max(4096)
const number = z.number().min(-1000000000).max(1000000000)
const count = z.number().int().min(0).max(2147483647)
const range = z.object({ min: number.optional(), max: number.optional() })
const fingerprint = z.object({
  text: z.object({ content: z.string().max(65536), matchType: z.enum(['exact', 'contains', 'startsWith']).optional() }),
  visual: z
    .object({
      fontSize: range.optional(),
      fontWeight: z.object({ min: number.optional() }).optional(),
      positionY: range.optional(),
      isAboveFold: z.boolean().optional()
    })
    .optional(),
  element: z
    .object({
      tagName: z.string().max(200).optional(),
      candidateTags: z.array(z.string().max(200)).max(100).optional()
    })
    .optional(),
  structure: z
    .object({
      siblingIndex: count.optional(),
      siblingCount: count.optional(),
      parentChain: z.string().max(4096).optional(),
      depth: count.optional()
    })
    .optional(),
  semantics: z
    .object({
      id: z.string().max(4096).nullable().optional(),
      dataAttributes: z
        .record(z.string().max(4096))
        .refine((value) => Object.keys(value).length <= 100)
        .nullable()
        .optional(),
      ariaRole: z.string().max(4096).nullable().optional(),
      ariaLabel: z.string().max(4096).nullable().optional(),
      meaningfulClasses: z.array(z.string().max(200)).max(100).optional()
    })
    .optional(),
  content: z
    .object({ charCount: count.optional(), wordCount: count.optional(), textPrefix: z.string().max(65536).optional() })
    .optional()
})
export const editorTargetSchema = z.object({
  selector,
  targeting: z
    .discriminatedUnion('mode', [
      z.object({
        mode: z.literal('selector'),
        selector,
        fallbackSelector: selector.optional(),
        fingerprint: fingerprint.optional()
      }),
      z.object({
        mode: z.literal('fingerprint'),
        selector: selector.optional(),
        fallbackSelector: selector.optional(),
        fingerprint
      })
    ])
    .optional()
})
const candidateSchema = z.object({
  key: z.string().min(1).max(200),
  tagName: z.string().min(1).max(200).optional(),
  target: editorTargetSchema,
  label: z.string().min(1).max(200),
  text: z.string().max(65536),
  occupiedBy: z.array(z.string().min(1).max(128)).max(2000),
  ancestorKeys: z.array(z.string().min(1).max(200)).max(199).optional()
})
export const campaignEditorContextSchema = z
  .object({
    campaignId: z.string().min(1).max(128),
    pageUrl: z
      .string()
      .url()
      .max(8192)
      .refine((value) => ['http:', 'https:'].includes(new URL(value).protocol)),
    source: z.enum(['live', 'proxy']),
    activeAudience: z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('original'), versionId: versionIdSchema.exclude(['A']).optional() }),
      z.object({ kind: z.literal('variant'), id: z.string().min(1).max(128), versionId: versionIdSchema.optional() })
    ]),
    selected: z
      .object({
        target: editorTargetSchema,
        tagName: z.string().min(1).max(200).optional(),
        label: z.string().min(1).max(200),
        originalText: z.string().max(65536)
      })
      .optional(),
    candidates: z.array(candidateSchema).max(200),
    matrix: z
      .object({
        pageUrl: z.string().url().max(8192),
        fill: z.boolean().optional(),
        selection: matrixSelectionSchema.optional(),
        emptyCells: z.array(matrixCellSchema).max(2000),
        truncated: z.boolean()
      })
      .optional(),
    omitted: count
  })
  .refine((value) => jsonBytes(value) <= 131072, 'The editor context exceeds 128 KiB.')
export type CampaignEditorContext = z.infer<typeof campaignEditorContextSchema>
const encoder = new TextEncoder()
function jsonBytes(value: unknown): number {
  return encoder.encode(JSON.stringify(value)).length
}

export async function captureCampaignEditorContext(input: {
  campaign: Campaign
  pageUrl: string
  source: 'live' | 'proxy'
  audience: Audience
  versionId?: VersionId
  selected: { target: TargetDescriptor; detail: ElementDetail | null } | null
  canvas: Pick<CanvasController, 'candidates' | 'inspectElement'>
  report: ResolutionReport | null
  matrix?: Pick<Matrix, 'rows' | 'columns' | 'cells'>
  refreshMatrix?: () => Promise<void>
  groups?: readonly GroupCatalogEntry[]
  selection?: MatrixSelectionRequest
}): Promise<CampaignEditorContext> {
  const context: CampaignEditorContext = {
    campaignId: input.campaign.id,
    pageUrl: input.pageUrl,
    source: input.source,
    activeAudience:
      input.audience.kind === 'variant'
        ? { ...input.audience, ...(input.versionId ? { versionId: input.versionId } : {}) }
        : { kind: 'original', ...(input.versionId && input.versionId !== 'A' ? { versionId: input.versionId } : {}) },
    candidates: [],
    omitted: 0
  }
  if (input.matrix || input.selection) {
    context.matrix = { pageUrl: input.pageUrl, emptyCells: [], truncated: false }
  }
  const selectedElements = new Set<ElementId>()
  if (input.selection && context.matrix) {
    if (input.selection.pageUrl !== input.pageUrl) {
      throw new Error('The page changed. Select the cells again before asking Righty.')
    }
    const cells = z.array(matrixCellSchema).min(1).max(2000).parse(input.selection.cells)
    const audienceIds = new Set(cells.map((cell) => cell.audienceId))
    const groups = input.groups ?? []
    const audiences = [...audienceIds].map((id) => {
      const audience = input.campaign.variants.find((variant) => variant.id === id)
      if (!audience) {
        throw new Error('A selected audience no longer exists. Select the cells again.')
      }
      const groupIds = new Set(audienceTerms(audience).terms.map((term) => term.groupId))
      return {
        id,
        name: audienceDisplayName(audience, groups).slice(0, 200),
        ...(audience.test
          ? {
              versions: audienceVersions(audience, audienceDisplayName(audience, groups)).map(
                ({ versionId, label }) => ({
                  versionId,
                  label: label.slice(0, 200)
                })
              )
            }
          : {}),
        groups: groups
          .filter((group) => groupIds.has(group.id))
          .map((group) => ({
            id: group.id,
            name: group.name.slice(0, 200)
          }))
      }
    })
    for (const cell of cells) {
      const audience = input.campaign.variants.find((variant) => variant.id === cell.audienceId)
      if (!audience || !audienceVersions(audience).some((version) => version.versionId === (cell.versionId ?? 'A'))) {
        throw new Error('A selected version no longer exists. Select the cells again.')
      }
      if (!isElementId(cell.elementId)) {
        throw new Error('A selected element is unavailable. Select the cells again.')
      }
      selectedElements.add(cell.elementId)
    }
    context.matrix.selection = matrixSelectionSchema.parse({
      cells,
      audiences,
      ...(input.selection.opening ? { opening: true } : {})
    })
    if (selectedElements.size > 200 || jsonBytes(context) > 100000) {
      throw new Error('This selection is too large for Righty. Select fewer cells.')
    }
  }
  if (input.selected && !input.selection) {
    context.selected = {
      target: editorTargetSchema.parse(input.selected.target),
      ...(input.selected.detail ? { tagName: input.selected.detail.tag.toLowerCase() } : {}),
      label: (input.selected.detail?.label || input.selected.target.selector).slice(0, 200),
      originalText: (input.selected.detail?.originalText ?? '').slice(0, 65536)
    }
    while (jsonBytes(context) > 120000 && context.selected.originalText.length) {
      context.selected.originalText = context.selected.originalText.slice(
        0,
        Math.floor(context.selected.originalText.length / 2)
      )
    }
  }
  campaignEditorContextSchema.parse(context)
  const actions = new Map(campaignActions(input.campaign).map((entry) => [entry.key, entry.audience.id]))
  const seen = new Set<ElementId>()
  const ancestors = new Map<string, string[]>()
  async function addCandidate(element: ElementId) {
    if (seen.has(element)) {
      return
    }
    seen.add(element)
    if (context.candidates.length >= 200) {
      context.omitted++
      return
    }
    const row = input.selection ? input.matrix?.rows.find((item) => item.element === element) : undefined
    const fact = input.report?.elements.find((item) => item.id === element)
    const detail = row?.target
      ? {
          target: row.target,
          label: row.label,
          originalText: row.original,
          tag: fact?.tag,
          ancestors: (fact?.ancestors ?? []).map((id) => ({ id }))
        }
      : await inspectCandidate(element, row?.label ?? fact?.label ?? 'selected element')
    const occupiedBy = [
      ...new Set(
        input.report?.inventory.flatMap((entry) => {
          const audienceId = actions.get(entry.key)
          return audienceId && entry.elements.includes(element) ? [audienceId] : []
        })
      )
    ]
    const parsed = candidateSchema.safeParse({
      key: element,
      tagName: detail.tag?.toLowerCase(),
      target: detail.target,
      label: (detail.label || detail.target.selector).slice(0, 200),
      text: detail.originalText.slice(0, 65536),
      occupiedBy,
      ancestorKeys: []
    })
    if (!parsed.success) {
      context.omitted++
      return
    }
    context.candidates.push(parsed.data)
    if (selectedElements.has(element)) {
      const remaining = selectedElements.size - context.candidates.length
      const budget = 131040 - remaining * 1024
      while (jsonBytes(context) > budget && parsed.data.text.length) {
        parsed.data.text = parsed.data.text.slice(0, Math.floor(parsed.data.text.length / 2))
      }
    }
    if (jsonBytes(context) > 131040) {
      context.candidates.pop()
      context.omitted++
    } else {
      ancestors.set(
        element,
        detail.ancestors.map((ancestor) => ancestor.id)
      )
    }
  }
  async function inspectCandidate(element: ElementId, label: string): Promise<ElementDetail> {
    try {
      return await input.canvas.inspectElement(element)
    } catch (failure) {
      if (
        !input.selection ||
        !(failure instanceof Error) ||
        failure.message !== 'This element is no longer on the page.'
      ) {
        throw failure
      }
      if (input.refreshMatrix) {
        await input.refreshMatrix()
        try {
          return await input.canvas.inspectElement(element)
        } catch (retryFailure) {
          if (!(retryFailure instanceof Error) || retryFailure.message !== 'This element is no longer on the page.') {
            throw retryFailure
          }
        }
      }
      throw new Error(`The ${label} isn't on this page right now. Reload the page and try again.`, { cause: failure })
    }
  }
  for (const element of selectedElements) {
    await addCandidate(element)
  }
  if (!input.selection) {
    for (const row of input.matrix?.rows ?? []) {
      if (row.element) {
        await addCandidate(row.element)
      }
    }
    let cursor: string | undefined
    do {
      const page = await input.canvas.candidates(cursor)
      for (const fact of page.items) {
        await addCandidate(fact.id)
      }
      cursor = page.next ?? undefined
    } while (cursor)
  }
  const candidateKeys = new Set(context.candidates.map((candidate) => candidate.key))
  for (;;) {
    for (const candidate of context.candidates) {
      candidate.ancestorKeys = [...new Set(ancestors.get(candidate.key)?.filter((key) => candidateKeys.has(key)))]
    }
    if (jsonBytes(context) <= 131040 || !context.candidates.length) {
      break
    }
    const removed = context.candidates.pop()
    if (removed) {
      candidateKeys.delete(removed.key)
      context.omitted++
    }
  }
  if ([...selectedElements].some((element) => !candidateKeys.has(element))) {
    throw new Error('The selected cells could not fit in Righty’s context. Select fewer cells.')
  }
  if (input.matrix && context.matrix && !input.selection) {
    const textCandidates = new Set(
      context.candidates
        .filter((candidate) => candidate.tagName && supportsMatrixText(candidate.tagName))
        .map((candidate) => candidate.key)
    )
    const pairs = new Set<string>()
    for (const row of input.matrix.rows) {
      for (const column of input.matrix.columns) {
        const cell = input.matrix.cells.get(row.id)?.get(column.id)
        const audienceId = column.audienceId ?? column.id
        if (
          column.readOnly ||
          column.locked ||
          input.campaign.variants.find((audience) => audience.id === audienceId)?.test ||
          !cell ||
          cell.actions.length
        ) {
          continue
        }
        if (!row.element || !candidateKeys.has(row.element)) {
          context.matrix.truncated = true
          continue
        }
        if (!textCandidates.has(row.element)) {
          continue
        }
        const pairKey = JSON.stringify([column.id, row.element])
        if (pairs.has(pairKey)) {
          continue
        }
        pairs.add(pairKey)
        if (context.matrix.emptyCells.length >= 2000) {
          context.matrix.truncated = true
          continue
        }
        context.matrix.emptyCells.push({ audienceId, elementId: row.element })
        if (jsonBytes(context) > 131040) {
          context.matrix.emptyCells.pop()
          context.matrix.truncated = true
        }
      }
    }
  }
  return campaignEditorContextSchema.parse(context)
}
